import React, { useEffect, useState } from 'react';
import { referralsAPI } from '../api';
import { Gift, Users, CheckCircle, Clock, TrendingUp, Save, Settings, Trophy } from 'lucide-react';

const GOLD = '#D4AF37';
const INK = '#0F0F0F';
const MUTED = '#595959';
const LINE = '#E5E5E5';
const GREEN = '#166534';

const AdminReferrals = () => {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({ referrer_reward: '', referee_reward: '', min_order: '', validity_days: '', active: true });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, st] = await Promise.all([referralsAPI.adminSettings(), referralsAPI.adminStats()]);
      setSettings(s); setStats(st);
      setForm({
        referrer_reward: String(s.referrer_reward ?? 200),
        referee_reward: String(s.referee_reward ?? 100),
        min_order: String(s.min_order ?? 999),
        validity_days: String(s.validity_days ?? 90),
        active: s.active !== false,
      });
    } catch (e) { setToast({ msg: e.message, type: 'error' }); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const save = async () => {
    setSaving(true);
    try {
      await referralsAPI.updateAdminSettings({
        referrer_reward: parseInt(form.referrer_reward),
        referee_reward: parseInt(form.referee_reward),
        min_order: parseInt(form.min_order),
        validity_days: parseInt(form.validity_days),
        active: form.active,
      });
      showToast('Referral settings saved');
      load();
    } catch (e) { showToast(e.message, 'error'); }
    setSaving(false);
  };

  const Card = ({ label, value, color, icon: Icon }) => (
    <div className="bg-white border p-3" style={{ borderColor: LINE }}>
      <div className="flex items-center gap-1 mb-1">
        <Icon className="w-3 h-3" style={{ color }} strokeWidth={1.5} />
        <span className="text-[9px] tracking-[0.1em] uppercase" style={{ color: MUTED }}>{label}</span>
      </div>
      <p className="text-[16px]" style={{ fontFamily: 'Playfair Display, serif', color: INK, fontWeight: 500 }}>{value}</p>
    </div>
  );

  if (loading) return <p className="p-6 text-center text-[11px]" style={{ color: MUTED }}>Loading…</p>;

  return (
    <div data-testid="admin-referrals" className="pb-6">
      {toast && (
        <div className="fixed top-16 right-4 z-50 px-3 py-2 text-[11px] shadow-lg"
          style={{ backgroundColor: toast.type === 'error' ? '#991B1B' : GREEN, color: '#FFF' }}>
          {toast.msg}
        </div>
      )}

      {/* Status chip */}
      <div className="mb-3">
        <span className="flex items-center gap-1 px-2 py-1 text-[10px] inline-flex"
          style={{
            backgroundColor: settings?.active ? 'rgba(22,101,52,0.08)' : 'rgba(153,27,27,0.08)',
            color: settings?.active ? GREEN : '#991B1B',
          }}>
          <Gift className="w-2.5 h-2.5" strokeWidth={1.5} />
          Program: {settings?.active ? 'ACTIVE' : 'PAUSED'}
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <Card label="Total Referrals" value={stats?.total_referrals || 0} color={GOLD} icon={Users} />
        <Card label="Rewarded" value={stats?.rewarded_referrals || 0} color={GREEN} icon={CheckCircle} />
        <Card label="Coupons Issued" value={stats?.coupons_issued || 0} color="#991B1B" icon={Gift} />
        <Card label="Discount Given" value={`₹${(stats?.total_discount_given || 0).toLocaleString('en-IN')}`} color="#8B5CF6" icon={TrendingUp} />
      </div>

      {/* Settings form */}
      <div className="bg-white border p-4 mb-3" style={{ borderColor: LINE }}>
        <div className="flex items-center gap-2 mb-3">
          <Settings className="w-3.5 h-3.5" style={{ color: GOLD }} strokeWidth={1.5} />
          <p className="text-[10px] tracking-[0.12em] uppercase" style={{ color: MUTED }}>Reward Controls</p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[9px] tracking-[0.08em] uppercase mb-1 block" style={{ color: MUTED }}>Referrer Reward (₹)</label>
            <input data-testid="referrer-reward-input" type="number" value={form.referrer_reward}
              onChange={e => setForm({ ...form, referrer_reward: e.target.value })}
              className="w-full py-2 px-2 border text-[13px] focus:outline-none focus:border-[#0F0F0F]"
              style={{ borderColor: LINE, color: INK }} />
            <p className="text-[9px] mt-0.5" style={{ color: '#BFBFBF' }}>Given to inviter when friend pays advance</p>
          </div>
          <div>
            <label className="text-[9px] tracking-[0.08em] uppercase mb-1 block" style={{ color: MUTED }}>Referee Welcome Reward (₹)</label>
            <input data-testid="referee-reward-input" type="number" value={form.referee_reward}
              onChange={e => setForm({ ...form, referee_reward: e.target.value })}
              className="w-full py-2 px-2 border text-[13px] focus:outline-none focus:border-[#0F0F0F]"
              style={{ borderColor: LINE, color: INK }} />
            <p className="text-[9px] mt-0.5" style={{ color: '#BFBFBF' }}>Given to new user instantly on signup with code</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] tracking-[0.08em] uppercase mb-1 block" style={{ color: MUTED }}>Min Order ₹</label>
              <input data-testid="min-order-input" type="number" value={form.min_order}
                onChange={e => setForm({ ...form, min_order: e.target.value })}
                className="w-full py-2 px-2 border text-[13px] focus:outline-none focus:border-[#0F0F0F]"
                style={{ borderColor: LINE, color: INK }} />
            </div>
            <div>
              <label className="text-[9px] tracking-[0.08em] uppercase mb-1 block" style={{ color: MUTED }}>Validity (days)</label>
              <input data-testid="validity-days-input" type="number" value={form.validity_days}
                onChange={e => setForm({ ...form, validity_days: e.target.value })}
                className="w-full py-2 px-2 border text-[13px] focus:outline-none focus:border-[#0F0F0F]"
                style={{ borderColor: LINE, color: INK }} />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer pt-1">
            <input data-testid="active-toggle" type="checkbox" checked={form.active}
              onChange={e => setForm({ ...form, active: e.target.checked })} />
            <span className="text-[11px]" style={{ color: INK }}>Program Active (uncheck to pause all rewards)</span>
          </label>
          <button data-testid="save-settings-btn" onClick={save} disabled={saving}
            className="w-full py-2.5 text-[11px] tracking-[0.08em] uppercase text-white flex items-center justify-center gap-1.5 disabled:opacity-60"
            style={{ backgroundColor: INK }}>
            <Save className="w-3.5 h-3.5" strokeWidth={1.5} /> {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Top referrers */}
      <div className="bg-white border" style={{ borderColor: LINE }}>
        <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: LINE }}>
          <Trophy className="w-3 h-3" style={{ color: GOLD }} strokeWidth={1.5} />
          <p className="text-[10px] tracking-[0.12em] uppercase" style={{ color: MUTED }}>Top Referrers</p>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {(!stats?.top_referrers || stats.top_referrers.length === 0) ? (
            <p className="p-4 text-center text-[11px]" style={{ color: MUTED }}>No referrers yet</p>
          ) : stats.top_referrers.map((r, i) => (
            <div key={r.user_id} className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: '#F0F0F0' }}>
              <div className="flex items-center gap-2">
                <span className="text-[10px] w-5" style={{ color: GOLD, fontFamily: 'Playfair Display, serif' }}>#{i + 1}</span>
                <div>
                  <p className="text-[11px]" style={{ color: INK, fontWeight: 500 }}>{r.name}</p>
                  <p className="text-[9px]" style={{ color: MUTED }}>{r.referral_code} · {r.email}</p>
                </div>
              </div>
              <span className="text-[10px] px-1.5 py-0.5" style={{ backgroundColor: 'rgba(22,101,52,0.08)', color: GREEN }}>
                {r.successful_referrals} rewards
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminReferrals;
